import { Wallet } from '@injectivelabs/wallet-base/light'
import { TradingLayout, OrderbookLayout, TradingChartInterval } from '@/types'

type PersistedStateMap = Record<string, Record<string, any>>
const DEFAULT_CHART_CANDLE_BAR_SPACING = 9.2

export const stateToPersist = {
  app: {
    userState: {
      modalsViewed: [],
      bannersViewed: [],
      dontShowAgain: [],
      favoriteMarkets: [],
      seenStartTrading: false,
      marketSlippageIdMap: {},
      marketLeverageIdMap: {},

      preferences: {
        selectedLanguage: '',
        isHideBalances: false,
        authZManagement: false,
        chartShowPosition: true,
        isOrderbookVisible: false,
        playOrderFillSound: false,
        thousandsSeparator: false,
        isAutoMarketCloseEnabled: false,
        chartShowTradeHistory: true,
        skipTradeConfirmationModal: false,
        tradingLayout: TradingLayout.Left,
        orderbookLayout: OrderbookLayout.Default,
        isRfqFastExecutionEnabled: false,
        isRfqClientGatewayEnabled: false,
        tradingChartInterval: TradingChartInterval['1h'],
        chartZoomPreference: DEFAULT_CHART_CANDLE_BAR_SPACING
      }
    }
  },

  account: {
    subaccountId: ''
  },

  auth: {
    accessToken: '',
    expiresAt: null,
    signerAddress: '',
    granterAddress: '',
    refreshTokenExpiresAt: null
  },

  wallet: {
    autoSignSetup: {
      account: 'pending',
      authorization: 'pending'
    }
  },

  sharedGeo: {
    geoContinent: '',
    geoCountry: '',
    ipCountry: '',
    ipAddress: '',
    vpnCheckedTimestamp: 0
  },

  sharedAppConfig: {
    viewedAnnouncementIds: []
  },

  sharedWallet: {
    walletConnectStatus: '',
    hwAddresses: '',
    wallet: Wallet.Metamask,
    session: '',
    address: '',
    addresses: '',
    isReadOnly: false,
    injectiveAddress: '',
    addressConfirmation: '',
    turnkeyInjectiveAddress: '',

    authZ: {
      address: '',
      direction: '',
      injectiveAddress: '',
      defaultSubaccountId: ''
    },

    autoSign: {
      version: 0,
      publicKey: '',
      expiration: '',
      storageKey: '',
      isDeterministic: true,
      injectiveAddress: '',
      duration: ''
    },
    privateKey: ''
  }
} satisfies PersistedStateMap

export function buildPersistedState({
  state,
  storeId,
  existingState = {}
}: {
  storeId: string
  state: Record<string, any>
  existingState?: PersistedStateMap
}) {
  const persistedStoreState = getPersistedStoreState({ state, storeId })

  if (!persistedStoreState) {
    return
  }

  return {
    ...stateToPersist,
    ...existingState,
    [storeId]: persistedStoreState
  }
}

export function getPersistedStoreState({
  state,
  storeId
}: {
  storeId: string
  state: Record<string, any>
}) {
  const defaultState = (stateToPersist as PersistedStateMap)[storeId]

  if (!defaultState) {
    return
  }

  const persistedState = Object.keys(defaultState).reduce(
    (stateObj, key) => ({
      ...stateObj,
      [key]: Object.prototype.hasOwnProperty.call(state, key)
        ? state[key]
        : defaultState[key]
    }),
    {}
  )

  return sanitizePersistedState(storeId, persistedState)
}

export function sanitizePersistedState(
  storeId: string,
  state: Record<string, any>
) {
  if (storeId === 'app' && state.userState) {
    const defaultUserState = stateToPersist.app.userState

    return {
      ...state,
      userState: {
        ...defaultUserState,
        ...state.userState,
        preferences: {
          ...defaultUserState.preferences,
          ...state.userState.preferences
        }
      }
    }
  }

  if (storeId !== 'sharedWallet' || !state.autoSign) {
    return state
  }

  const autoSign = { ...state.autoSign }

  delete autoSign.privateKey
  delete autoSign.isConfirmed

  return {
    ...state,
    autoSign
  }
}
